import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { logger } from '../../../services/logger/logger';
import RobsInformationCard from '../../../components/robs/RobsInformationCard';
import RobsProductTable, { RobsProductTableRef } from '../../../components/robs/RobsProductTable';
import { RobProduct } from '../../../store/apiSlices/robs/robsApiParser';
import {
  useCreateRobMutation,
  useGetProductsByVesselQuery,
  useGetUpdatedProductByVesselQuery,
  useUpdateRobMutation,
} from '../../../store/apiSlices/robs/robsApis';
import { showSuccessToast } from '../../../components/common/BaseToaster';
import { useUnsavedChanges } from '../../../context/UnsavedChangesContext';
import UnsavedChangesModal from '../../../components/UnsavedChangesModal';
import { MetaOption } from '../../../types';
import dayjs from 'dayjs';
interface FormValues {
  vessel: string;
  manager: string;
  date: Date | string | any | null; // Date, string, or dayjs object, can be null
  remarks: string;
}

interface RobsProductListingContainerProps {
  setParams?: any;
  robsFormData: Partial<FormValues>;
  selectedType: string;
  onBack: () => void;
  vesselId?: number;
  vessels?: MetaOption[];
  managers?: MetaOption[];
  isEditMode?: boolean;
  editData?: any;
}

const RobsProductListingContainer: React.FC<RobsProductListingContainerProps> = ({
  setParams: _setParams,
  robsFormData,
  selectedType,
  onBack,
  vesselId,
  vessels,
  managers,
  isEditMode,
  editData,
}) => {
  // State management
  const [localProducts, setLocalProducts] = useState<RobProduct[]>([]);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const tableRef = useRef<RobsProductTableRef>(null);

  // Track user edits to detect unsaved changes
  const userEditsRef = useRef<Record<number, Partial<RobProduct>>>({});

  // Unsaved changes context
  const { registerUnsavedChanges, clearUnsavedChanges } = useUnsavedChanges();

  // Get vesselId from props or form data
  const effectiveVesselId = vesselId || (robsFormData.vessel ? parseInt(robsFormData.vessel) : 0);

  // API hooks
  const {
    data: products = [],
    isLoading: isLoadingProducts,
    refetch: refetchProducts,
  } = useGetProductsByVesselQuery(
    { vesselId: effectiveVesselId },
    { skip: !effectiveVesselId || effectiveVesselId === 0 },
  );

  const {
    data: updatedProducts = [],
    isLoading: isLoadingUpdatedProducts,
    refetch: refetchUpdatedProducts,
  } = useGetUpdatedProductByVesselQuery(
    { vesselId: effectiveVesselId, robId: editData?.id },
    {
      skip: !effectiveVesselId || effectiveVesselId === 0 || !editData?.id || !isEditMode,
    },
  );
  const [createRobs, { isLoading: isCreating }] = useCreateRobMutation();
  const [updateRobs, { isLoading: isUpdating }] = useUpdateRobMutation();

  // Effects
  useEffect(() => {
    if (isEditMode && updatedProducts.length > 0) {
      setLocalProducts(updatedProducts);
    } else if (products.length > 0) {
      setLocalProducts(products);
    }
  }, [products, updatedProducts, isEditMode]);

  // Generate robs info from form data
  const robsInfo = useMemo(
    () => ({
      vessel: robsFormData.vessel || '-',
      manager: robsFormData.manager || '-',
      date: robsFormData.date || '-',
      type: selectedType || '-',
      remarks: robsFormData.remarks || '-',
    }),
    [robsFormData, selectedType],
  );

  const handleSaveRobs = useCallback(
    async (products: any[]): Promise<void> => {
      try {
        const types: Record<string, string> = {
          'End of Management': 'EOM',
          EOM: 'EOM',
          ACQ: 'ACQ',
          'New vessel acquisition': 'NEW',
          standard: 'STD',
          Standard: 'STD',
          endOfManagement: 'EOM',
          newBuilding: 'NEW',
          secondHand: 'ACQ',
        };

        const payload = {
          vesselId: effectiveVesselId || 0,
          managerId: parseInt(robsFormData.manager || '') || 0,
          type: types[selectedType] || '',
          comments: robsFormData.remarks || '',
          eventDateValidForType: selectedType?.toLowerCase() === 'standard' ? true : false,
          robProducts: products?.map((itm) => ({
            productId: itm?.id,
            storageQuantity: Number(itm?.quantity),
            sumpTankQuantity: !itm?.isSumpTankMandatory ? 0 : Number(itm?.netValue),
            valueOfStorageQuantity: 0,
          })),
          ...(selectedType?.toLowerCase() === 'standard' && {
            eventDate: (() => {
              const rawDate = robsFormData.date;
              // Keep the user-selected calendar date stable and avoid timezone day shifts.
              if (!rawDate) return undefined;

              const datePart = (() => {
                if (typeof rawDate === 'string') {
                  const trimmed = rawDate.trim();

                  // Already in ISO date format (or ISO datetime with date prefix)
                  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
                    return trimmed.slice(0, 10);
                  }

                  // Handle DD/MM/YYYY from UI/text inputs
                  const dmyMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                  if (dmyMatch) {
                    const [, dd, mm, yyyy] = dmyMatch;
                    return `${yyyy}-${mm}-${dd}`;
                  }
                }

                const parsed = dayjs(rawDate);
                return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
              })();

              if (!datePart) return undefined;
              return `${datePart}T23:59:00.000Z`;
            })(),
          }),
        };

        if (isEditMode) {
          const res: any = await updateRobs({
            id: editData.id,
            payload,
          }).unwrap();
          showSuccessToast(res?.message);
          refetchUpdatedProducts();
          // Clear unsaved changes and navigate
          clearUnsavedChanges();
          userEditsRef.current = {};
        } else {
          const res: any = await createRobs(payload).unwrap();
          showSuccessToast(res?.message);
          refetchProducts();
          // Clear unsaved changes and navigate
          clearUnsavedChanges();
          userEditsRef.current = {};
        }
      } catch (error: any) {
        logger.error('Failed to save ROB', error as Error);
      }
    },
    [
      effectiveVesselId,
      robsFormData,
      selectedType,
      isEditMode,
      editData,
      updateRobs,
      createRobs,
      refetchUpdatedProducts,
      refetchProducts,
      clearUnsavedChanges,
    ],
  );

  // Track unsaved changes
  const hasUnsavedChanges = Object.keys(userEditsRef.current).length > 0;

  const handleSaveRobsRef = useRef(handleSaveRobs);
  useEffect(() => {
    handleSaveRobsRef.current = handleSaveRobs;
  }, [handleSaveRobs]);

  const stableOnSave = useCallback(async () => {
    if (handleSaveRobsRef.current && tableRef.current) {
      const products = tableRef.current.getProducts();
      await handleSaveRobsRef.current(products);
    }
  }, []);

  const handleDiscardChanges = useCallback(() => {
    // Clear user edits
    userEditsRef.current = {};
  }, []);

  // Register unsaved changes with context
  useEffect(() => {
    registerUnsavedChanges(hasUnsavedChanges, stableOnSave, handleDiscardChanges);
  }, [hasUnsavedChanges, registerUnsavedChanges, stableOnSave, handleDiscardChanges]);

  // In CREATE mode, mark as having unsaved changes when component mounts
  // This ensures warning shows even if user hasn't entered any product values yet
  // because reaching this screen means they've already filled the form
  useEffect(() => {
    if (!isEditMode) {
      // Mark as having unsaved changes by adding a dummy entry to userEditsRef
      // Using -1 as a special marker that won't conflict with real product IDs
      (userEditsRef.current as any)[-1] = { _formData: true };
    }
  }, [isEditMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      registerUnsavedChanges(false);
    };
  }, [registerUnsavedChanges]);

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowCancelModal(true);
    }
  }, [hasUnsavedChanges]);

  const handleCloseCancelModal = useCallback(() => {
    setShowCancelModal(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setShowCancelModal(false);
    handleDiscardChanges();
    clearUnsavedChanges();
  }, [handleDiscardChanges, clearUnsavedChanges]);

  // Track product changes
  const handleProductChange = useCallback(
    (productId: number, field: keyof RobProduct, value: any) => {
      // Store user edit in ref to track unsaved changes
      if (!userEditsRef.current[productId]) {
        userEditsRef.current[productId] = {};
      }
      userEditsRef.current[productId][field] = value;
    },
    [],
  );

  return (
    <>
      {/* Robs Information Card */}
      <RobsInformationCard
        robsInfo={robsInfo}
        onBack={onBack}
        vessels={vessels}
        managers={managers}
      />

      {/* Products Table */}
      <RobsProductTable
        ref={tableRef}
        onSave={handleSaveRobs}
        submitting={isCreating || isUpdating}
        products={localProducts}
        isLoading={isLoadingProducts || isLoadingUpdatedProducts}
        onCancel={handleCancel}
        isEditMode={isEditMode}
        selectedType={selectedType}
        onProductChange={handleProductChange}
      />

      <UnsavedChangesModal
        open={showCancelModal}
        onClose={handleCloseCancelModal}
        onLeaveWithoutSaving={handleConfirmCancel}
      />
    </>
  );
};

export default RobsProductListingContainer;
